import React from 'react';
import classNames from 'classnames';

import { GroupMembers } from './GroupMembers';
import { GroupHeading } from './GroupHeading';

import { GroupTypes } from 'helpers';
import { useGroupsSidebarState, useFeatureFlagState } from 'context';
import {
  trackingGroupExpand,
  trackingGroupCollapse,
} from 'services/analytics/interceptor/handlers/trackGroup';

import styles from './Group.module.scss';

interface GroupProps {
  /** @Group Group data */
  group?: Group;
  /** @boolean If the group is collapsed or expanded by default */
  collapsedByDefault?: boolean;
  /** @string The current user id */
  currentUserId?: string;
  /** @boolean If it's the "other people" group */
  isOtherPeople?: boolean;
  /** @boolean If it's the organization group */
  isOrganization?: boolean;
}

export const Group: React.FC<GroupProps> = ({
  group,
  collapsedByDefault,
  currentUserId,
  isOtherPeople,
  isOrganization,
}) => {
  const { groupsFeatureFlag } = useFeatureFlagState();

  /**
   * Determines if the group should render collapsed or expanded
   * If the group was just created it should expand
   */
  const getInitialCollapsed = (): boolean => {
    /**
     * If the group exists and it's id is the same as the last
     * created group, expand the group.
     */
    if (group && group.id === createdGroupId) {
      return false;
    }

    /**
     * If it's the org group or the other people group it should be expanded
     */
    if (isOrganization || isOtherPeople) {
      return false;
    }

    /**
     * If the groups feature is disabled or if there is no group it should collapse
     */
    if (!groupsFeatureFlag || !group) {
      return true;
    }

    return !!collapsedByDefault;
  };

  const members = group?.members || [];
  const { searchGroupId, createdGroupId } = useGroupsSidebarState();
  const [collapsed, setCollapsed] = React.useState(getInitialCollapsed());

  const classes = classNames(styles.groupWrapper, {
    [styles.searching]: searchGroupId && searchGroupId === group?.id,
  });

  /**
   * Toggles the group expanded/collapsed state
   * @param groupId
   */
  const toggleCollapse = React.useCallback(() => {
    if (collapsed) {
      setCollapsed(false);
      trackingGroupExpand({ name: group?.name || '', type: group?.groupType });
    } else {
      setCollapsed(true);
      trackingGroupCollapse({ name: group?.name || '', type: group?.groupType });
    }
  }, [group, collapsed]);

  /**
   * Filters the list of members so that the current user
   * does not appear on the group members list
   */
  const membersWithoutUser = React.useMemo(() => {
    return members.filter((member: User) => member.id !== currentUserId);
  }, [currentUserId, members]);

  /**
   * Renders the group
   */
  const renderGroup = () => {
    const members = membersWithoutUser;
    const allowAddMembers = !isOtherPeople && group?.groupType !== GroupTypes.ORGANISATION;

    return (
      <div className={classes}>
        <GroupHeading
          group={group}
          collapsed={collapsed}
          alternateStyle={group && !isOrganization && !isOtherPeople}
          toggleCollapse={toggleCollapse}
        />
        <GroupMembers
          group={group}
          members={members}
          collapsed={collapsed}
          allowAddMembers={allowAddMembers}
          alternateStyle={group && !isOrganization && !isOtherPeople}
        />
      </div>
    );
  };

  /**
   * Renders the group creation ui
   */
  const renderGroupCreate = () => {
    return (
      <div className={classes}>
        <GroupHeading collapsed={collapsed} toggleCollapse={toggleCollapse} />
      </div>
    );
  };

  return <>{!!group ? renderGroup() : renderGroupCreate()}</>;
};

export default Group;
